// apps/prompts/src/components/NotificationBell.tsx
import {
  AppIcon,
  IconBell,
  IconBolt,
  IconTrophy,
  IconBooks,
  IconCircleCheck,
  type AppIconProps,
} from '@neuroforge/ui'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '../hooks/useNotifications'

interface NotificationItem {
  id: string
  type: string
  title: string
  message: string | null
  status: string
  actionUrl: string | null
  createdAt: string
}

function formatNotifDate(dateStr: string): string {
  const now = Date.now()
  const date = new Date(dateStr).getTime()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'maintenant'
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}j`
}

const NOTIF_ICONS: Record<string, AppIconProps['icon']> = {
  NEW_PROMPT: IconBolt,
  BADGE_EARNED: IconTrophy,
  COURSE_PUBLISHED: IconBooks,
  LESSON_COMPLETED: IconCircleCheck,
  SYSTEM: IconBell,
}

export default function NotificationBell() {
  const { data } = useNotifications()
  const { data: unreadData } = useUnreadCount()
  const unreadCount = unreadData?.unreadCount ?? 0
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const notifications = (data?.data ?? []) as NotificationItem[]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="text-(--muted) hover:text-(--text) transition] relative flex h-11 w-11 items-center justify-center rounded-2xl"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} non lues)` : 'Notifications'}
      >
        <AppIcon icon={IconBell} size={20} aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="glass absolute right-0 z-50 mt-2 flex max-h-[420px] w-80 flex-col overflow-hidden rounded-2xl p-3 shadow-xl">
          <div className="border-(--border) flex items-center justify-between border-b px-2 pb-2">
            <p className="text-sm font-bold">Notifications</p>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-[10px] font-bold text-indigo-500 hover:underline disabled:opacity-50"
              >
                Tout marquer lu
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <AppIcon
                  icon={IconBell}
                  size={24}
                  className="text-(--muted) mx-auto mb-2"
                  aria-hidden
                />
                <p className="text-xs text-slate-400">Aucune notification</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => (
                <Link
                  key={notif.id}
                  to={notif.actionUrl ?? '/notifications'}
                  onClick={() => {
                    if (notif.status === 'UNREAD') markRead.mutate(notif.id)
                    setOpen(false)
                  }}
                  className={`block rounded-xl px-3 py-2.5 transition-colors ${
                    notif.status === 'UNREAD'
                      ? 'bg-indigo-500/5 hover:bg-indigo-500/10'
                      : 'hover:bg-(--bg-elevated)'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <AppIcon
                      icon={NOTIF_ICONS[notif.type] ?? IconBell}
                      size={20}
                      className="text-(--accent) shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="line-clamp-1 text-xs font-bold">{notif.title}</p>
                        {notif.status === 'UNREAD' && (
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        )}
                      </div>
                      {notif.message && (
                        <p className="mt-0.5 line-clamp-2 text-[10px] text-slate-400">
                          {notif.message}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-[10px] text-slate-400">
                      {formatNotifDate(notif.createdAt)}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="border-(--border) border-t pt-2">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block py-1 text-center text-xs font-bold text-indigo-500 hover:underline"
            >
              Voir toutes les notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
